import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const STATUS_COLORS = {
  TRIAL:     { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  ACTIVE:    { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  PAST_DUE:  { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  GRACE:     { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  EXPIRED:   { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  CANCELLED: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' }
};

const fmtINR = (n) => {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.EXPIRED;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: 0.3
    }}>
      {status}
    </span>
  );
}

const PLAN_CARDS = [
  {
    key: 'starter',
    name: 'Starter',
    description: 'Essential modules for small properties',
    color: '#6b7280',
    highlight: false
  },
  {
    key: 'professional',
    name: 'Professional',
    description: 'Full-featured suite for growing hotels',
    color: '#2563eb',
    highlight: true
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    description: 'Complete platform for hotel chains',
    color: '#7c3aed',
    highlight: false
  }
];

function MySubscription({ token, selectedProperty, role, permissions }) {
  const { isMobile } = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sub, setSub] = useState(null);
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('MONTHLY');
  const [pricePreview, setPricePreview] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/subscription/my/subscription', { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable');
      const json = await res.json();
      if (res.status === 404) {
        setSub(null);
      } else if (!res.ok) {
        throw new Error(json?.error || 'Failed to load subscription');
      } else {
        setSub(json.subscription || json);
        if (json.subscription || json) {
          setBillingPeriod((json.subscription || json).billing_period || 'MONTHLY');
          setSelectedModules(
            ((json.subscription || json).items || [])
              .filter((it) => it.status !== 'REMOVED')
              .map((it) => it.module_code || it.module_id)
          );
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chainId, propertyId]);

  const loadModules = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/subscription/my/modules', { headers: headers() });
      const json = await res.json();
      if (res.ok) setModules(json.modules || json || []);
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chainId, propertyId]);

  const loadPlans = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/subscription/my/plans', { headers: headers() });
      const json = await res.json();
      if (res.ok) setPlans(json.plans || json || []);
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chainId, propertyId]);

  useEffect(() => {
    loadSubscription();
    loadModules();
    loadPlans();
  }, [loadSubscription, loadModules, loadPlans]);

  const calculatePrice = async (moduleList, period) => {
    if (!moduleList || moduleList.length === 0) {
      setPricePreview(null);
      return;
    }
    setCalcLoading(true);
    try {
      const res = await fetch('/api/v1/subscription/my/calculate-price', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ modules: moduleList, billing_period: period || billingPeriod })
      });
      const json = await res.json();
      if (res.ok) setPricePreview(json);
      else setPricePreview(null);
    } catch (_) {
      setPricePreview(null);
    } finally {
      setCalcLoading(false);
    }
  };

  const toggleModule = (code) => {
    const next = selectedModules.includes(code)
      ? selectedModules.filter((c) => c !== code)
      : [...selectedModules, code];
    setSelectedModules(next);
    calculatePrice(next, billingPeriod);
  };

  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
    calculatePrice(selectedModules, period);
  };

  const handleSubscribe = async (trial = false) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        plan_id: selectedPlan || undefined,
        modules: selectedModules,
        billing_period: billingPeriod,
        start_trial: trial
      };
      const res = await fetch('/api/v1/subscription/my/subscribe', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to subscribe');
      setSuccess(trial ? 'Free trial started!' : 'Subscription created!');
      loadSubscription();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddModule = async (moduleCode) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch('/api/v1/subscription/my/add-module', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ module_code: moduleCode })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to add module');
      setSuccess(`Module added.`);
      loadSubscription();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveModule = async (moduleCode) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch('/api/v1/subscription/my/remove-module', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ module_code: moduleCode })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to remove module');
      setSuccess(`Module removed.`);
      loadSubscription();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeBilling = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch('/api/v1/subscription/my/change-billing-period', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ billing_period: billingPeriod })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to change billing period');
      setSuccess(`Billing period changed to ${billingPeriod}.`);
      loadSubscription();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const enabledModuleCodes = sub
    ? (sub.items || []).filter((it) => it.status !== 'REMOVED').map((it) => it.module_code || it.module_id)
    : [];

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 20px', color: 'var(--theme-text, #1f2937)', fontSize: isMobile ? 18 : 22 }}>
        My Subscription
      </h2>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 6, color: '#16a34a', fontSize: 13
        }}>
          {success}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

      {/* No subscription — Get Started */}
      {!loading && !sub && (
        <>
          <div style={{
            textAlign: 'center', padding: isMobile ? '24px 12px' : '40px 24px',
            background: 'var(--theme-surface, #fff)',
            border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, marginBottom: 24
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 20, color: 'var(--theme-text, #1f2937)' }}>
              Get Started with EASEPMS
            </h3>
            <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: 14 }}>
              Choose a plan and select the modules you need.
            </p>

            {/* Plan cards */}
            <div style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 16, marginBottom: 32, maxWidth: 900, margin: '0 auto 32px'
            }}>
              {(plans.length > 0 ? plans : PLAN_CARDS).map((plan) => {
                const key = plan.plan_id || plan.key || plan.name;
                const isSelected = selectedPlan === key;
                const color = plan.color || 'var(--theme-primary, #2563eb)';
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedPlan(key)}
                    style={{
                      background: 'var(--theme-surface, #fff)',
                      border: isSelected ? `2px solid ${color}` : '1px solid var(--theme-border, #e5e7eb)',
                      borderRadius: 10, padding: isMobile ? 16 : 24, cursor: 'pointer',
                      transition: 'border-color 0.15s',
                      boxShadow: isSelected ? `0 0 0 3px ${color}22` : 'none'
                    }}
                  >
                    <div style={{
                      fontSize: 16, fontWeight: 700, color: color, marginBottom: 6
                    }}>
                      {plan.name || plan.plan_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                      {plan.description || ''}
                    </div>
                    {plan.price != null && (
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--theme-text, #1f2937)' }}>
                        {fmtINR(plan.price)}<span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>/mo</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Module selection */}
          <div style={{
            background: 'var(--theme-surface, #fff)',
            border: '1px solid var(--theme-border, #e5e7eb)',
            borderRadius: 12, padding: isMobile ? 16 : 24, marginBottom: 24
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--theme-text, #1f2937)' }}>
              Select Modules
            </h3>

            {/* Billing period toggle */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
              {['MONTHLY', 'QUARTERLY', 'YEARLY'].map((p) => (
                <button
                  key={p}
                  onClick={() => handleBillingPeriodChange(p)}
                  style={{
                    padding: '7px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: '1px solid var(--theme-border, #d1d5db)',
                    background: billingPeriod === p ? 'var(--theme-primary, #2563eb)' : 'var(--theme-surface, #fff)',
                    color: billingPeriod === p ? '#fff' : 'var(--theme-text, #374151)',
                    borderRadius: p === 'MONTHLY' ? '6px 0 0 6px' : p === 'YEARLY' ? '0 6px 6px 0' : 0,
                    marginLeft: p !== 'MONTHLY' ? -1 : 0
                  }}
                >
                  {p}
                </button>
              ))}
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12
            }}>
              {modules.map((mod) => {
                const code = mod.module_code || mod.id;
                const checked = selectedModules.includes(code);
                return (
                  <div
                    key={code}
                    onClick={() => toggleModule(code)}
                    style={{
                      border: checked
                        ? '2px solid var(--theme-primary, #2563eb)'
                        : '1px solid var(--theme-border, #e5e7eb)',
                      borderRadius: 8, padding: 14, cursor: 'pointer',
                      background: checked ? '#eff6ff' : 'var(--theme-surface, #fff)',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--theme-text, #1f2937)' }}>
                        {mod.name || mod.module_name || code}
                      </div>
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        style={{ accentColor: 'var(--theme-primary, #2563eb)', width: 16, height: 16 }}
                      />
                    </div>
                    {mod.description && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{mod.description}</div>
                    )}
                    {mod.price != null && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-primary, #2563eb)', marginTop: 6 }}>
                        {fmtINR(mod.price)}<span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>/mo</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Price preview */}
            {pricePreview && (
              <div style={{
                marginTop: 20, padding: 16, background: '#f0fdf4',
                border: '1px solid #bbf7d0', borderRadius: 8
              }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Price Summary</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Subtotal: </span>
                    <strong>{fmtINR(pricePreview.subtotal)}</strong>
                  </div>
                  {pricePreview.discount > 0 && (
                    <div>
                      <span style={{ fontSize: 12, color: '#16a34a' }}>Discount: </span>
                      <strong style={{ color: '#16a34a' }}>-{fmtINR(pricePreview.discount)}</strong>
                    </div>
                  )}
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Tax: </span>
                    <strong>{fmtINR(pricePreview.tax)}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Total: </span>
                    <strong style={{ fontSize: 18, color: 'var(--theme-primary, #2563eb)' }}>
                      {fmtINR(pricePreview.total)}
                    </strong>
                  </div>
                </div>
              </div>
            )}

            {calcLoading && <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>Calculating price...</div>}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
              <button
                onClick={() => handleSubscribe(true)}
                disabled={actionLoading || selectedModules.length === 0}
                style={{
                  padding: '10px 24px', borderRadius: 8,
                  border: '1px solid var(--theme-primary, #2563eb)',
                  background: 'transparent', color: 'var(--theme-primary, #2563eb)',
                  fontSize: 14, fontWeight: 600, cursor: actionLoading ? 'default' : 'pointer',
                  opacity: actionLoading || selectedModules.length === 0 ? 0.5 : 1
                }}
              >
                Start Free Trial
              </button>
              <button
                onClick={() => handleSubscribe(false)}
                disabled={actionLoading || selectedModules.length === 0}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: 'var(--theme-primary, #2563eb)', color: '#fff',
                  fontSize: 14, fontWeight: 600, cursor: actionLoading ? 'default' : 'pointer',
                  opacity: actionLoading || selectedModules.length === 0 ? 0.5 : 1
                }}
              >
                {actionLoading ? 'Processing...' : 'Subscribe'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Existing subscription */}
      {!loading && sub && (
        <>
          {/* Subscription info card */}
          <div style={{
            background: 'var(--theme-surface, #fff)',
            border: '1px solid var(--theme-border, #e5e7eb)',
            borderRadius: 12, padding: isMobile ? 16 : 24, marginBottom: 20
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              flexWrap: 'wrap', gap: 12, marginBottom: 16
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--theme-text, #1f2937)' }}>
                  {sub.plan_name || 'Current Plan'}
                </h3>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Billing: {sub.billing_period || '-'}
                </div>
              </div>
              <StatusBadge status={sub.status} />
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: 12, fontSize: 13
            }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Status</div>
                <div style={{ fontWeight: 600 }}>{sub.status}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Next Billing</div>
                <div style={{ fontWeight: 600 }}>{fmtDate(sub.current_period_end)}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Amount</div>
                <div style={{ fontWeight: 600 }}>{fmtINR(sub.amount)}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Started</div>
                <div style={{ fontWeight: 600 }}>{fmtDate(sub.created_at)}</div>
              </div>
            </div>

            {/* Billing period toggle */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Change Billing Period</div>
              <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                {['MONTHLY', 'QUARTERLY', 'YEARLY'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setBillingPeriod(p)}
                    style={{
                      padding: '6px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      border: '1px solid var(--theme-border, #d1d5db)',
                      background: billingPeriod === p ? 'var(--theme-primary, #2563eb)' : 'var(--theme-surface, #fff)',
                      color: billingPeriod === p ? '#fff' : 'var(--theme-text, #374151)',
                      borderRadius: p === 'MONTHLY' ? '6px 0 0 6px' : p === 'YEARLY' ? '0 6px 6px 0' : 0,
                      marginLeft: p !== 'MONTHLY' ? -1 : 0
                    }}
                  >
                    {p}
                  </button>
                ))}
                {billingPeriod !== (sub.billing_period || 'MONTHLY') && (
                  <button
                    onClick={handleChangeBilling}
                    disabled={actionLoading}
                    style={{
                      marginLeft: 10, padding: '6px 14px', borderRadius: 6, border: 'none',
                      background: 'var(--theme-primary, #2563eb)', color: '#fff',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      opacity: actionLoading ? 0.6 : 1
                    }}
                  >
                    Apply
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Modules grid */}
          <div style={{
            background: 'var(--theme-surface, #fff)',
            border: '1px solid var(--theme-border, #e5e7eb)',
            borderRadius: 12, padding: isMobile ? 16 : 24
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--theme-text, #1f2937)' }}>
              Modules
            </h3>
            <div style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12
            }}>
              {modules.map((mod) => {
                const code = mod.module_code || mod.id;
                const enabled = enabledModuleCodes.includes(code);
                return (
                  <div
                    key={code}
                    style={{
                      border: enabled
                        ? '2px solid var(--theme-primary, #2563eb)'
                        : '1px solid var(--theme-border, #e5e7eb)',
                      borderRadius: 8, padding: 14,
                      background: enabled ? '#eff6ff' : 'var(--theme-surface, #fff)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--theme-text, #1f2937)' }}>
                        {mod.name || mod.module_name || code}
                      </div>
                      {enabled ? (
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 10,
                          background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600
                        }}>
                          ENABLED
                        </span>
                      ) : null}
                    </div>
                    {mod.description && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{mod.description}</div>
                    )}
                    {mod.price != null && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-primary, #2563eb)', marginBottom: 8 }}>
                        {fmtINR(mod.price)}<span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>/mo</span>
                      </div>
                    )}
                    <button
                      onClick={() => enabled ? handleRemoveModule(code) : handleAddModule(code)}
                      disabled={actionLoading}
                      style={{
                        padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        border: enabled ? '1px solid #dc2626' : '1px solid var(--theme-primary, #2563eb)',
                        background: 'transparent',
                        color: enabled ? '#dc2626' : 'var(--theme-primary, #2563eb)',
                        opacity: actionLoading ? 0.5 : 1
                      }}
                    >
                      {enabled ? 'Remove' : 'Add Module'}
                    </button>
                  </div>
                );
              })}
              {modules.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 30, color: '#6b7280' }}>
                  No modules available.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MySubscription;
